import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * usePlanState — fetches the user's feature-gating snapshot from the backend.
 *
 * Shape: {
 *   is_premium: bool,
 *   is_admin_exempt: bool,
 *   plan: "free" | "trial" | "premium",
 *   trial_active: bool,
 *   trial_expired: bool,
 *   trial_ends_at: string | null,
 *   grace: { in_grace, days_remaining, grace_expires_at, started_at },
 *   seats, seat_limit,
 *   free_grace_days_total: number,
 *   climbers: [{ climber_id, climber_name, completed_expeditions, free_full_expedition_used }]
 * }
 *
 * Loading state surfaces as `state === null`.
 */
export const usePlanState = () => {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setState(null);
      return null;
    }
    setLoading(true);
    try {
      const r = await api.get("/account/plan-state");
      setState(r.data);
      return r.data;
    } catch {
      setState(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Convenience helpers
  const usageForClimber = (climberId) =>
    (state?.climbers || []).find((c) => c.climber_id === climberId) || null;

  const isFree = !!state && !state.is_premium;

  return { state, loading, refresh, isFree, usageForClimber };
};

export default usePlanState;
